import { Injectable } from '@angular/core';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { IV_THEME_MODES, ROTATE_TYPES, SELECTORS, STORAGE_NAMES, VIEW_TYPES } from '@root/app.config';
import { BackgroundConfig, CroppedBackgroundConfig, CropperSettings } from '@root/app.interfaces';
import { IndexedDBService } from '@core/services/indexed-db/indexed-db.service';
import { RotateDeviceModalComponent } from '@shared/modals/rotate-device-modal/rotate-device-modal.component';
import { PassDataService } from '@core/services/pass-data/pass-data.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ModalService } from '@core/services/modal/modal.service';
import { Router } from '@angular/router';
import Cropper from 'cropperjs';
import { ShareService } from '@core/services/share-data/share-data.service';
import { debounceTime, fromEvent, Subscription } from 'rxjs';
import { getScreenOrientation } from '@core/helpers/orientation.utils';

@Injectable({
	providedIn: 'root',
})
export class BackgroundService {
	isMobile: boolean = this.passDataService.isMobileOrTablet;
	backgroundTheme: string;
	backgroundData: BackgroundConfig;
	cropperSettings: CropperSettings;

	orientationChangeDelay = 300; // Only with such a delay it works correctly on Safari
	orientationChangeSubscription: Subscription;

	constructor(
		private router: Router,
		private matDialog: MatDialog,
		private shareService: ShareService,
		private modalService: ModalService,
		private passDataService: PassDataService,
		private indexedDBService: IndexedDBService,
		private sessionStorageService: SessionStorageService,
	) {
		this.orientationChangeHandler();
		this.setOrientationChangeSubscription();
	}

	orientationChangeHandler(): void {
		window.onorientationchange = () => {
			const { isHomePage, isImageVisualisation, isSampleProject } = this.getPageContextFlags();

			if (isImageVisualisation && isHomePage) {
				const orientation = getScreenOrientation();

				if (isSampleProject && !this.passDataService.isLiveOpened) {
					this.backgroundData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);
					const splitId = this.backgroundData.id.split('_');
					splitId.pop();
					splitId.push(orientation);
					const id = splitId.join('_');

					this.indexedDBService.getImage(STORAGE_NAMES.zip_image_visualisation_background, id).then((image) => {
						if (image) {
							window.URL.revokeObjectURL(image.src);

							this.backgroundData.night_src = image.night_src;
							this.uploadFile(image.blob);
						} else if (orientation !== this.backgroundData.image_orientation) {
							const { type, index } = this.backgroundData;

							this.loaderSwitcher(true);
							this.getSampleProjectPromise(type, index, orientation).then(([dayBlob, nightBlob]) => {
								this.backgroundData.night_src = nightBlob ? window.URL.createObjectURL(nightBlob) : null;
								this.uploadFile(dayBlob);
							});
						}
					});
				}
			}
		};
	}

	setOrientationChangeSubscription(): void {
		// Subscribe to orientation change events to display the "Rotate Device" popup.
		// This is handled separately because it should trigger independently of the scene background.
		this.rotateDeviceModalHandler();

		this.orientationChangeSubscription?.unsubscribe();
		this.orientationChangeSubscription = fromEvent(window, 'orientationchange')
			.pipe(debounceTime(this.orientationChangeDelay))
			.subscribe(() => {
				const { isHomePage, isImageVisualisation, isSampleProject } = this.getPageContextFlags();

				if (isHomePage && isImageVisualisation) {
					this.openRotateDeviceModal(isSampleProject);
				}
			});
	}

	getPageContextFlags(): { isHomePage: boolean; isImageVisualisation: boolean; isSampleProject: boolean } {
		const isHomePage = this.router.url.includes('/home');
		const isImageVisualisation = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_view_type) === VIEW_TYPES.image_visualisation;
		const isSampleProject = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_iv_type) === 'project_sample';

		return { isHomePage, isImageVisualisation, isSampleProject };
	}

	getSampleProjectImage(currentBackgroundImageId): Promise<any> {
		this.backgroundData = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_image_visualisation_background);

		return new Promise((resolve) => {
			const uploadFile = (file: File | Blob) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				reader.onload = () => (this.backgroundData.base64 = reader.result);

				const blob = new Blob([file]);
				const image = new Image();

				image.src = window.URL.createObjectURL(blob);
				image.onload = () => {
					const { width, height, src } = image;

					this.backgroundData = {
						...this.backgroundData,
						src,
						width,
						height,
						image_orientation: getScreenOrientation(),
						id: `${this.backgroundData.type}_${this.backgroundData.index}_${getScreenOrientation()}`,
						blob: file,
					};

					this.indexedDBService.saveImage(this.backgroundData, STORAGE_NAMES.zip_image_visualisation_background);
					resolve(this.backgroundData);
				};
			};

			const [type, index, orientation] = currentBackgroundImageId.split('_');
			this.getSampleProjectPromise(type, index, orientation).then(([dayBlob, nightBlob]) => {
				this.backgroundData.night_src = nightBlob ? window.URL.createObjectURL(nightBlob) : null;
				uploadFile(dayBlob);
			});
		});
	}

	getSampleProjectPromise(type: string, index: number, orientation: string): Promise<Blob[]> {
		const fetchImage = (url: string) => fetch(url).then((res) => res.blob());

		const projectSamples = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_config)?.iv_images;
		const isOutdoor = type === 'outdoor';

		const selectProjectPromises = [
			fetchImage(projectSamples[type].day[orientation][index]),
			(isOutdoor || this.sessionStorageService.getSession(STORAGE_NAMES.zip_country_code) === 'sg') &&
				fetchImage(projectSamples[type].night[orientation][index]),
		];

		return Promise.all(selectProjectPromises);
	}

	uploadIVFile(file: File | Blob): Promise<BackgroundConfig> {
		return new Promise((resolve) => {
			const imageProperties = {};
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				imageProperties['base64'] = reader.result;
			};

			const image = new Image();
			image.src = window.URL.createObjectURL(file);
			image.onload = async () => {
				imageProperties['src'] = image.src;
				imageProperties['blob'] = file;
				imageProperties['height'] = image.height;
				imageProperties['width'] = image.width;

				if (this.cropperSettings) {
					const croppedProperties = await this.getCroppedImage(image);
					resolve({ ...imageProperties, ...croppedProperties } as BackgroundConfig);
				} else {
					resolve(imageProperties as BackgroundConfig);
				}
			};
		});
	}

	getCroppedImage(image: HTMLImageElement): Promise<CroppedBackgroundConfig> {
		return new Promise((resolve) => {
			const { cropper_data, cropper_box_data, cropper_canvas_data, cropper_container_data } = this.cropperSettings;
			delete this.cropperSettings;

			const containerElement = document.createElement('div');
			containerElement.appendChild(image);

			const ready = () => {
				cropper.setData(cropper_data).setCropBoxData(cropper_box_data).setCanvasData(cropper_canvas_data);

				cropper.getCroppedCanvas().toBlob((file) => {
					const croppedImage = new Image();
					croppedImage.src = window.URL.createObjectURL(file);

					croppedImage.onload = () => {
						resolve({
							cropped_src: croppedImage.src,
							height: croppedImage.height,
							width: croppedImage.width,
						});
					};
				});
			};

			const cropper = new Cropper(image, {
				viewMode: 0,
				minContainerHeight: cropper_container_data.height,
				minContainerWidth: cropper_container_data.width,
				restore: false,
				ready,
			});
		});
	}

	async getIVImageFromSettings(imageSrc): Promise<any> {
		return fetch(imageSrc)
			.then((res) => res.blob())
			.then((blob) => this.uploadIVFile(blob))
			.catch((err) => console.error(err));
	}

	async getIVSampleImageFromSettings(type: string, index: number, orientation: string): Promise<BackgroundConfig> {
		try {
			const [dayBlob, nightBlob] = await this.getSampleProjectPromise(type, index, orientation);
			const image = await this.uploadIVFile(dayBlob);

			image.night_src = nightBlob ? window.URL.createObjectURL(nightBlob) : null;

			return image;
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	uploadImage(url: string): void {
		this.loaderSwitcher(true);
		fetch(url)
			.then((res) => res.blob())
			.then((blob) => this.uploadFile(blob))
			.catch((err) => console.error(err));
	}

	uploadFile(file: File | Blob) {
		const image = new Image();

		image.src = window.URL.createObjectURL(file);
		image.onload = () => {
			const { width, height, src } = image;

			this.backgroundData = {
				...this.backgroundData,
				src,
				width,
				height,
				image_orientation: getScreenOrientation(),
				id: `${this.backgroundData.type}_${this.backgroundData.index}_${getScreenOrientation()}`,
				blob: file,
			};

			const backgroundConfig = { ...this.backgroundData };
			delete backgroundConfig.base64;

			this.sessionStorageService.setBlindData(backgroundConfig, STORAGE_NAMES.zip_image_visualisation_background);

			this.loaderSwitcher(false);
			this.setBackground(this.backgroundData);

			this.indexedDBService.saveImage(this.backgroundData, STORAGE_NAMES.zip_image_visualisation_background);
		};
	}

	loaderSwitcher(status: boolean): void {
		document.querySelector(SELECTORS.model_viewer).classList.toggle('loader', status);
	}

	setBackground(backgroundData: BackgroundConfig): void {
		const canvas = document.getElementById(SELECTORS.render_canvas) as HTMLElement;
		const src = this.backgroundTheme === IV_THEME_MODES.day ? backgroundData.src : backgroundData.night_src;
		canvas.style.backgroundImage = `url(${src})`;
	}

	openRotateDeviceModal(isSampleProject: boolean): void {
		if (this.passDataService.isLiveOpened || this.shareService.getSelectOpeningModeValue()) {
			return;
		}

		const isOpen = this.matDialog.getDialogById(RotateDeviceModalComponent.ID);
		const orientation = getScreenOrientation();
		const rotateType = orientation === 'landscape' ? ROTATE_TYPES.to_portrait : ROTATE_TYPES.to_landscape;

		if (this.isMobile && !isOpen) {
			this.matDialog.open(
				RotateDeviceModalComponent,
				this.modalService.getConfig(RotateDeviceModalComponent.ID, { rotateType, isSampleProject }),
			);
		}
	}

	rotateDeviceModalHandler(): void {
		// Sometimes 'Rotate Device' Popup can be opened from ViewSwitcherComponent.
		this.matDialog.afterOpened.subscribe((matDialog) => {
			if (matDialog.id === RotateDeviceModalComponent.ID) {
				this.orientationChangeSubscription?.unsubscribe();

				matDialog.afterClosed().subscribe(this.setOrientationChangeSubscription.bind(this));
			}
		});
	}
}
